import React from 'react';
import PropTypes from 'prop-types';
import { useOptimizelyFeature } from 'client/hooks/useOptimizelyFeature';
import { CreateNewEpisodeButton } from 'components/PublicWebsiteNav/components/CreateNewEpisodeButton';
import { ADS_PAGE_PATH } from 'client/screens/AdsByAnchorScreen/constants';
import { useIsSaiOrVodcast } from 'client/hooks/span/useIsSaiOrVodcast';
import { getShowNewEpisodeButton } from 'components/PublicWebsiteNav/utils';
import { paths } from 'client/shared/Paths';
import { useT } from 'client/i18n/hooks/useT';
import { useLocale } from 'client/i18n/hooks/useLocale';
import { getIsEPEnabled } from 'modules/FeatureFlags';
import classNames from 'classnames';
import buttonStyles from 'client/shared/Button/styles.sass';
import { addColorSet } from '@spotify-internal/encore-web';
import Link from '../Link';
import { AccountNavItems } from './components/AccountNavItems';
import { SignUpLoginNavItems } from './components/SignUpLoginNavItems';
import { HamburgerIcon } from './components/HamburgerIcon';
import { MobileNavContainer } from './components/MobileNav/MobileNavContainer';
import { NavLinks } from './components/NavLinks';
import { clickNavLink, clickedNavLinkGoogleAnalytics } from './events';
import { SIGNED_IN_LINKS, SIGNED_OUT_LINKS } from './constants';
import { useFeatureFlagsCtx } from '../../contexts/FeatureFlags';
import { AnchorLogoWithWordmark } from '../svgs/AnchorLogoWithWordmark';
import { SpotifyLogo } from '../svgs/SpotifyLogo';
import { useSponsorships } from '../../hooks/useSponsorships';
import { getCampaignsArray } from '../../screens/SupportersScreen/utils';
import { I18NLanguageSelector } from '../I18NLanguageSelector';
import {
  ContentBackground,
  ContentWrapper,
  MobileNav,
  NavWrapper,
  DesktopNav,
  LogoContainer,
  MainContent,
  SideLinks,
  AccountLinks,
  MainNav,
} from './styles';

const MARKETING_PAGE_PATHS = [
  paths.anchor.home,
  paths.anchor.features,
  paths.anchor.switch,
  paths.anchor.switchForm,
  ADS_PAGE_PATH,
];

function getIsMarketingPage(matchedPathname) {
  return MARKETING_PAGE_PATHS.includes(matchedPathname);
}

const getIsACampaignAwaitingRecording = (campaigns = []) =>
  campaigns.some(campaign => campaign.campaignStatus === 'awaitingRecording');

const getNavLinks = ({
  user,
  campaigns,
  isAllowedToSeeMoneyNavItem,
  showNetworkLinks,
  isSaiOrVodcast,
  isEPEnabled,
  isScalableAnalyticsEnabled,
}) => {
  if (!user) return SIGNED_OUT_LINKS;
  const displayNavLinks = [];
  const signedInLinks = isScalableAnalyticsEnabled
    ? SIGNED_IN_LINKS.map(link =>
        link.label === 'Dashboard'
          ? {
              type: 'link',
              label: 'Dashboard',
              key: 'dashboard',
              to: '/dashboard/analytics',
            }
          : link
      )
    : SIGNED_IN_LINKS;
  for (const navLink of signedInLinks) {
    const { key } = navLink;
    switch (key) {
      case 'money':
        if (isEPEnabled || isSaiOrVodcast) break;
        if (isAllowedToSeeMoneyNavItem)
          displayNavLinks.push({
            ...navLink,
            isShowingNotificationBadge: getIsACampaignAwaitingRecording(
              campaigns
            ),
          });
        break;
      case 'network':
        if (isEPEnabled) break;
        if (isSaiOrVodcast) break;
        if (showNetworkLinks) displayNavLinks.push(navLink);
        break;
      case 'dashboard':
        if (isSaiOrVodcast) break;
        displayNavLinks.push(navLink);
        break;
      default:
        displayNavLinks.push(navLink);
        break;
    }
  }
  return displayNavLinks;
};

/**
 * The ability to hard-refresh is a workaround for the case of client-side
 * navigating from the profile page to the dashboard; the isProfilePage boolean
 * doesn't update so the header becomes the wrong color. [TODO: https://anchorfm.atlassian.net/browse/WHEEL-419]
 * Implement a real fix for this.
 */
const Logo = ({ isSignedIn, isHardRefresh = false }) => {
  const url = isSignedIn ? '/dashboard' : '/';
  const linkProps = {
    [isHardRefresh ? 'href' : 'to']: url,
  };
  return (
    <Link
      {...linkProps}
      onClick={() => {
        clickedNavLinkGoogleAnalytics('Home');
        clickNavLink({ link: 'icon' });
      }}
    >
      <AnchorLogoWithWordmark
        ariaLabel="Anchor logo links to the homepage"
        includeSpotify={true}
      />
    </Link>
  );
};

const PublicWebsiteNav = ({
  onLogOut,
  user,
  shareLinkPath,
  podcastNetwork,
  onClickNavItem,
  pathname,
  isMobileNavVisible,
  toggleMobileWebsiteNavVisible,
  podcastName,
  podcastImage,
  profileColor,
  isProfilePage,
  showNetworkLinks,
}) => {
  const t = useT();
  const { enableI18N } = useLocale();
  const {
    state: { featureFlags },
  } = useFeatureFlagsCtx();
  const {
    campaigns: campaignsObject,
    activationLifeCycleState,
  } = useSponsorships();
  const campaigns = campaignsObject ? getCampaignsArray(campaignsObject) : [];
  const [isScalableAnalyticsEnabled] = useOptimizelyFeature(
    'scalable_analytics_ui'
  );
  const isSaiOrVodcast = useIsSaiOrVodcast();
  // fallback to hostDomain is for server-side rendering; added to user session at login
  const isEPEnabled =
    getIsEPEnabled(featureFlags) || !!(user && user.hostDomain);

  const canShowMoneyPage = true;
  const isAllowedToSeeMoneyNavItem =
    (activationLifeCycleState &&
      activationLifeCycleState !== 'ineligibleForSponsorshipsFeature') ||
    canShowMoneyPage;
  const navLinks = getNavLinks({
    user,
    campaigns,
    isAllowedToSeeMoneyNavItem,
    showNetworkLinks,
    isSaiOrVodcast,
    isEPEnabled,
    isScalableAnalyticsEnabled,
  });

  const isFeaturesPage = pathname === '/features';
  const isMarketingPage = getIsMarketingPage(pathname);
  const isSticky = isMarketingPage;
  const isTransparent = isMarketingPage;
  const isSignedIn = !!user;
  const isHardRefresh = isProfilePage;

  return (
    <NavWrapper
      className={addColorSet('mutedAccent')}
      isSticky={isSticky}
      isTransparent={isTransparent}
      hasShadow={!isFeaturesPage}
    >
      <ContentBackground
        profileColor={profileColor}
        isTransparent={isTransparent}
      >
        <ContentWrapper isHidingPrimaryNav={isProfilePage && !user}>
          <MainNav>
            <DesktopNav>
              <LogoContainer>
                {isEPEnabled ? (
                  <Link to="/dashboard" title="EP Home">
                    <SpotifyLogo fillColor="#fff" width="100px" height="40px" />
                  </Link>
                ) : (
                  <Logo isSignedIn={isSignedIn} isHardRefresh={isHardRefresh} />
                )}
              </LogoContainer>
              <MainContent>
                <NavLinks
                  links={navLinks}
                  pathname={pathname}
                  isTransparent={isTransparent || isProfilePage}
                  isScalableAnalyticsEnabled={isScalableAnalyticsEnabled}
                />
              </MainContent>
              <SideLinks>
                {user ? (
                  <AccountLinks>
                    {getShowNewEpisodeButton(pathname) &&
                      (isSaiOrVodcast ? (
                        <Link
                          to="/dashboard/episode/new"
                          onClick={() => clickNavLink({ link: 'new_episode' })}
                          className={classNames(
                            buttonStyles.button,
                            buttonStyles.onDark
                          )}
                        >
                          {t(
                            'PublicWebsiteNavFooter.NewEpisodeButtonText',
                            'New Episode',
                            'Button to open "New Episode" nav dropdown'
                          )}
                        </Link>
                      ) : (
                        <CreateNewEpisodeButton />
                      ))}
                    <AccountNavItems
                      showNetworkLinks={showNetworkLinks}
                      shareLinkPath={shareLinkPath}
                      onClickNavItem={onClickNavItem}
                      podcastNetwork={podcastNetwork}
                      onLogOut={onLogOut}
                      podcastName={podcastName}
                      podcastImage={podcastImage}
                      isSaiOrVodcast={isSaiOrVodcast}
                    />
                    {enableI18N && <I18NLanguageSelector isDesktop />}
                  </AccountLinks>
                ) : (
                  <AccountLinks>
                    <SignUpLoginNavItems pathname={pathname} />
                    {enableI18N && <I18NLanguageSelector isDesktop />}
                  </AccountLinks>
                )}
              </SideLinks>
            </DesktopNav>
            <MobileNav>
              <Logo isSignedIn={isSignedIn} isHardRefresh={isHardRefresh} />
              <HamburgerIcon onClick={toggleMobileWebsiteNavVisible} />
              {isMobileNavVisible && (
                <MobileNavContainer
                  pathname={pathname}
                  links={navLinks}
                  isSignedIn={isSignedIn}
                  onClickClose={toggleMobileWebsiteNavVisible}
                  onLogOut={onLogOut}
                  isScalableAnalyticsEnabled={isScalableAnalyticsEnabled}
                />
              )}
            </MobileNav>
          </MainNav>
        </ContentWrapper>
      </ContentBackground>
    </NavWrapper>
  );
};

PublicWebsiteNav.propTypes = {
  onLogOut: PropTypes.func.isRequired,
  user: PropTypes.shape({
    imageUrl: PropTypes.string,
    userId: PropTypes.number,
    email: PropTypes.string,
    name: PropTypes.string,
  }),
  shareLinkPath: PropTypes.string,
  podcastNetwork: PropTypes.string,
  onClickNavItem: PropTypes.func.isRequired,
  pathname: PropTypes.string,
  isMobileNavVisible: PropTypes.bool,
  toggleMobileWebsiteNavVisible: PropTypes.func.isRequired,
  podcastName: PropTypes.string,
  podcastImage: PropTypes.string,
};

PublicWebsiteNav.defaultProps = {
  podcastNetwork: null,
  shareLinkPath: null,
  user: null,
  pathname: null,
  isMobileNavVisible: false,
  podcastName: null,
  podcastImage: null,
};

export { PublicWebsiteNav as default, PublicWebsiteNav };
